import { formatFileSize } from '../i18n/fileSize';
import { Trans, useLingui } from '@lingui/react/macro';
import { useState, useEffect, useCallback, useRef } from 'react';
import { FolderOutlined, FileImageOutlined, FileTextOutlined, FileOutlined, UploadOutlined, FolderAddOutlined, SearchOutlined } from '@ant-design/icons';
import type { ChannelFile, ChannelFileSearchPage, ChannelFileSearchResult } from '../types';
import * as api from '../lib/api';
import { toUploadFile } from '../hosts/web/files';
import { FileViewer } from './FileViewer';
import MarkdownEditor from './MarkdownEditor';

interface Props {
  channelId: string;
  readOnly?: boolean;
}

const searchLimit = 20;

function FileIcon({ file }: { file: Pick<ChannelFile, 'is_directory' | 'mime_type' | 'name'> }) {
  const { t } = useLingui();
  if (file.is_directory) return <FolderOutlined aria-label={t`Folder`} />;
  if (file.mime_type?.startsWith('image/')) return <FileImageOutlined aria-label={t`Image file`} />;
  if (file.name.endsWith('.md') || file.mime_type?.startsWith('text/')) return <FileTextOutlined aria-label={t`Text file`} />;
  return <FileOutlined aria-label={t`File`} />;
}

export default function FilesPanel({ channelId, readOnly = false }: Props) {
  const { t, i18n } = useLingui();
  const [files, setFiles] = useState<ChannelFile[]>([]);
  const [path, setPath] = useState<{ id: string | null; name: string }[]>([{ id: null, name: 'Root' }]);
  const [selectedFile, setSelectedFile] = useState<ChannelFile | null>(null);
  const [editingFile, setEditingFile] = useState<{ file: ChannelFile; content: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOffset, setSearchOffset] = useState(0);
  const [searchPage, setSearchPage] = useState<ChannelFileSearchPage | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searchRefresh, setSearchRefresh] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: ChannelFile } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentParentId = path[path.length - 1]!.id;

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listChannelFiles(channelId, currentParentId ?? undefined);
      setFiles(result);
    } catch {
      setFiles([]);
    }
    setLoading(false);
  }, [channelId, currentParentId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  useEffect(() => {
    if (!searchQuery) {
      setSearchPage(null);
      setSearchFailed(false);
      setSearchLoading(false);
      return;
    }
    const controller = new AbortController();
    setSearchLoading(true);
    setSearchFailed(false);
    api.searchChannelFiles(channelId, searchQuery, {
      offset: searchOffset,
      limit: searchLimit,
      signal: controller.signal,
    }).then(page => {
      setSearchPage(page);
      setSearchLoading(false);
    }).catch(() => {
      if (controller.signal.aborted) return;
      setSearchPage(null);
      setSearchFailed(true);
      setSearchLoading(false);
    });
    return () => controller.abort();
  }, [channelId, searchOffset, searchQuery, searchRefresh]);

  useEffect(() => {
    setPath([{ id: null, name: 'Root' }]);
    setSelectedFile(null);
    setEditingFile(null);
    setSearchDraft('');
    setSearchQuery('');
    setSearchOffset(0);
  }, [channelId]);

  const refreshFiles = useCallback(() => {
    void loadFiles();
    setSearchRefresh(value => value + 1);
  }, [loadFiles]);

  const handleFileClick = (file: ChannelFile) => {
    if (file.is_directory) {
      setPath(prev => [...prev, { id: file.id, name: file.name }]);
    } else {
      setSelectedFile(file);
    }
  };

  const handleBreadcrumb = (index: number) => {
    setPath(prev => prev.slice(0, index + 1));
  };

  const handleUpload = async (fileList: FileList) => {
    for (const file of Array.from(fileList)) {
      await api.uploadChannelFile(channelId, await toUploadFile(file), currentParentId ?? undefined);
    }
    refreshFiles();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleMkdir = async () => {
    const name = prompt(t`Folder name:`);
    if (!name?.trim()) return;
    await api.createChannelDirectory(channelId, name.trim(), currentParentId ?? undefined);
    refreshFiles();
  };

  const handleDelete = async (file: ChannelFile) => {
    if (!confirm(t`Delete ${file.name}?`)) return;
    await api.deleteChannelFile(channelId, file.id);
    refreshFiles();
  };

  const handleRename = async (file: ChannelFile) => {
    const newName = prompt(t`Rename to:`, file.name);
    if (!newName?.trim() || newName.trim() === file.name) return;
    try {
      await api.renameChannelFile(channelId, file.id, newName.trim());
      refreshFiles();
    } catch (err: unknown) {
      alert(err instanceof api.ApiError && err.status === 409 ? t`A file with that name already exists` : t`Rename failed`);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, file: ChannelFile) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, file });
  };

  const handleEdit = (file: ChannelFile, content: string) => {
    setSelectedFile(null);
    setEditingFile({ file, content });
  };

  const handleSaveEdit = async (content: string) => {
    if (!editingFile) return;
    await api.updateChannelFile(channelId, editingFile.file.id, content);
    setEditingFile(null);
    refreshFiles();
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = searchDraft.trim();
    setSearchOffset(0);
    setSearchQuery(query);
    setSearchRefresh(value => value + 1);
  };

  const clearSearch = () => {
    setSearchDraft('');
    setSearchQuery('');
    setSearchOffset(0);
  };

  const openSearchResult = (result: ChannelFileSearchResult) => {
    setSelectedFile({
      id: result.id,
      user_id: result.owner_user_id,
      channel_id: result.channel_id,
      parent_id: null,
      name: result.name,
      is_directory: result.is_directory,
      mime_type: result.mime_type,
      size_bytes: result.size_bytes,
      source: result.source,
      source_message_id: result.source_message_id || null,
      created_at: result.created_at,
      updated_at: result.updated_at,
    });
  };

  return (
    <div
      className="files-panel"
      onDragOver={readOnly ? undefined : (e => { e.preventDefault(); setDragging(true); })}
      onDragLeave={readOnly ? undefined : (() => setDragging(false))}
      onDrop={readOnly ? undefined : handleDrop}
      onClick={() => setContextMenu(null)}
    >
      <div className="files-panel-header">
        <h3><Trans>Files</Trans></h3>
        {!readOnly && <><button className="files-btn" onClick={handleMkdir}><FolderAddOutlined aria-hidden="true" /> <Trans>New folder</Trans></button>
        <button className="files-btn" onClick={() => fileInputRef.current?.click()}><UploadOutlined aria-hidden="true" /> <Trans>Upload</Trans></button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => e.target.files && handleUpload(e.target.files)}
        /></>}
      </div>
      <form className="files-search" onSubmit={handleSearch}>
        <SearchOutlined aria-hidden="true" />
        <input
          type="search"
          value={searchDraft}
          maxLength={200}
          aria-label={t`Search files`}
          placeholder={t`Search files`}
          onChange={event => setSearchDraft(event.target.value)}
        />
        {searchQuery && <button type="button" onClick={clearSearch}><Trans>Clear</Trans></button>}
        <button type="submit"><Trans>Search</Trans></button>
      </form>

      {!searchQuery && path.length > 1 && (
        <div style={{ padding: '4px 12px', fontSize: '0.8em', color: 'var(--text-secondary)' }}>
          {path.map((p, i) => (
            <span key={i}>
              {i > 0 && ' / '}
              <span
                style={{ cursor: 'pointer', textDecoration: i < path.length - 1 ? 'underline' : 'none' }}
                onClick={() => handleBreadcrumb(i)}
              >
                {p.id === null ? t`Root` : p.name}
              </span>
            </span>
          ))}
        </div>
      )}

      {searchQuery ? (
        searchLoading ? (
          <div className="files-empty"><Trans>Searching…</Trans></div>
        ) : searchFailed ? (
          <div className="files-empty"><Trans>File search failed</Trans></div>
        ) : !searchPage || searchPage.files.length === 0 ? (
          <div className="files-empty"><Trans>No matching files</Trans></div>
        ) : (
          <>
            <div className="file-tree files-search-results">
              {searchPage.files.map(result => (
                <button
                  type="button"
                  key={result.id}
                  className="file-tree-item files-search-result"
                  onClick={() => openSearchResult(result)}
                >
                  <span className="file-tree-icon"><FileIcon file={result} /></span>
                  <span className="files-search-copy">
                    <span className="file-tree-name">{result.name}</span>
                    <span className="files-search-path">{result.path}</span>
                    {result.content_excerpt && <span className="files-search-excerpt">{result.content_excerpt}</span>}
                  </span>
                  <span className="file-tree-size">{formatFileSize(i18n, result.size_bytes)}</span>
                </button>
              ))}
            </div>
            <div className="files-search-pagination">
              <button
                type="button"
                disabled={searchPage.offset === 0}
                onClick={() => setSearchOffset(Math.max(0, searchPage.offset - searchPage.limit))}
              >
                <Trans>Previous page</Trans>
              </button>
              <span>{searchPage.offset + 1}–{searchPage.offset + searchPage.files.length} / {searchPage.total}</span>
              <button
                type="button"
                disabled={!searchPage.has_more}
                onClick={() => setSearchOffset(searchPage.offset + searchPage.limit)}
              >
                <Trans>Next page</Trans>
              </button>
            </div>
          </>
        )
      ) : dragging ? (
        <div className="files-drop-zone"><Trans>Drop files here to upload</Trans></div>
      ) : loading ? (
        <div className="files-empty"><Trans>Loading…</Trans></div>
      ) : files.length === 0 ? (
        <div className="files-empty">
          <p><Trans>No files yet</Trans></p>
          {!readOnly && <p><Trans>Upload or drag files here</Trans></p>}
        </div>
      ) : (
        <div className="file-tree">
          {files.map(file => (
            <div
              key={file.id}
              className="file-tree-item"
              onClick={() => handleFileClick(file)}
              onContextMenu={readOnly ? undefined : (e => handleContextMenu(e, file))}
            >
              <span className="file-tree-icon"><FileIcon file={file} /></span>
              <span className="file-tree-name">{file.name}</span>
              {!file.is_directory && (
                <span className="file-tree-size">{formatFileSize(i18n, file.size_bytes)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {contextMenu && (
        <div className="files-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
          {contextMenu.file.is_directory && (
            <div className="files-context-menu-item" onClick={() => { handleFileClick(contextMenu.file); setContextMenu(null); }}>
              <Trans>Open</Trans>
            </div>
          )}
          <div className="files-context-menu-item" onClick={() => { handleRename(contextMenu.file); setContextMenu(null); }}>
            <Trans>Rename</Trans>
          </div>
          <div className="files-context-menu-item danger" onClick={() => { handleDelete(contextMenu.file); setContextMenu(null); }}>
            <Trans>Delete</Trans>
          </div>
        </div>
      )}

      {selectedFile && (
        <FileViewer
          file={selectedFile}
          channelId={channelId}
          onClose={() => setSelectedFile(null)}
          onEdit={!readOnly && selectedFile.name.endsWith('.md') ? handleEdit : undefined}
        />
      )}

      {editingFile && (
        <MarkdownEditor
          initialContent={editingFile.content}
          fileName={editingFile.file.name}
          onSave={handleSaveEdit}
          onCancel={() => setEditingFile(null)}
        />
      )}
    </div>
  );
}
